import { useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { StatusBadge } from '@/components/StatusBadge'
import {
  BreakGlassScreen,
  DeviceRegisterScreen,
  IpDeniedScreen,
  RoleSelectScreen,
  SessionExpiredScreen,
} from './AuthScreens'

/**
 * A1 auth ekranlari showcase'i (`/dev/auth`).
 *
 * Bu ekranlar endi HAQIQIY ma'lumotga ulangan (soxta prop yo'q):
 *   A1.03 qurilma      → joriy adminning kirish-nuqtalari (GET /admin/account/devices)
 *   A1.04 break-glass  → JONLI JIT grantlar + so'rov (GET/POST /admin/jit/*)
 *   A1.06 IP cheklovi  → JONLI IP-blok reyestri (GET /admin/ip-blocks)
 *   A1.07 rol tanlash  → JONLI rol katalogi (GET /admin/roles)
 * Har biri o'z ma'lumotini o'zi tortadi; bo'sh holat honest ko'rsatiladi.
 *
 * Login (A1.01) / Lock (A1) / Session-expired (A1.05) bu yerda faqat
 * showcase — ular jonli oqimda ham ko'rinadi.
 */

type DemoKey = 'A1.03' | 'A1.04' | 'A1.05' | 'A1.06' | 'A1.07'

interface DemoEntry {
  id: DemoKey
  label: string
  /** Jonli oqimga ulanганmi yoki yetim (server yo'q). */
  wired: boolean
  render: () => React.ReactNode
}

export function AuthDemoScreen() {
  const t = useT()
  const [active, setActive] = useState<DemoKey | null>(null)

  const entries: DemoEntry[] = [
    {
      id: 'A1.03',
      label: t.auth.deviceTitle,
      wired: true,
      render: () => <DeviceRegisterScreen />,
    },
    {
      id: 'A1.04',
      label: t.auth.breakGlassTitle,
      wired: true,
      render: () => <BreakGlassScreen />,
    },
    {
      id: 'A1.05',
      label: t.auth.sessionExpiredTitle,
      wired: true,
      render: () => <SessionExpiredScreen onContinue={() => setActive(null)} />,
    },
    {
      id: 'A1.06',
      label: t.auth.ipDeniedTitle,
      wired: true,
      render: () => <IpDeniedScreen />,
    },
    {
      id: 'A1.07',
      label: t.auth.roleSelectTitle,
      wired: true,
      render: () => <RoleSelectScreen />,
    },
  ]

  const current = entries.find((e) => e.id === active)

  if (current) {
    return (
      <div className="relative min-h-screen">
        <button
          type="button"
          onClick={() => setActive(null)}
          className="absolute top-3 left-3 z-10 flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-secondary hover:text-primary"
        >
          <ArrowLeft size={13} />
          {t.auth.demoBackToList}
        </button>
        {current.render()}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface-0 p-6">
      <div className="mx-auto max-w-2xl">
        <h1 className="text-lg font-semibold text-primary">{t.auth.demoTitle}</h1>
        <p className="mt-1 mb-4 text-sm text-secondary">{t.auth.demoDesc}</p>

        <ul className="space-y-1.5">
          {entries.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => setActive(e.id)}
                className="flex w-full items-center gap-3 rounded-md border border-border-default bg-surface-2 px-3 py-2.5 text-left hover:border-accent hover:bg-accent-subtle"
              >
                <span className="w-14 shrink-0 font-mono text-sm text-tertiary">{e.id}</span>
                <span className="min-w-0 flex-1 truncate text-base text-primary">{e.label}</span>
                <StatusBadge
                  status={e.wired ? 'active' : 'review'}
                  label={e.wired ? t.auth.demoLiveNote : t.auth.demoOrphanNote}
                  className={cn('shrink-0', !e.wired && 'opacity-90')}
                />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
